import { useEffect, useState } from 'react';
import { CalendarCheck, Loader2, Landmark } from 'lucide-react';
import { formatMoeda } from '@fiodecobre/shared';
import { api } from '../../lib/api';
import { useDialog } from '../DialogProvider';
import { ModalResultadoEmissao, type ResultadoEmissaoData } from './ModalResultadoEmissao';

// "Fechar o mes" da Venda CM (DA-383, cartao RDS 12.3). Consolida os venda_cm em aberto do cliente
// numa nota UNICA de venda comum (Consumidor ou A4) + 1 CR por pedido (comissao no vendedor de cada
// um). Pagamento e UMA forma: Pix, Dinheiro (a vista -> Movimento de Caixa) ou Crediario (a receber).
// Nao passa pelo caixa e nao baixa estoque de novo. A nota emite no backend; se falhar, o fechamento
// ja valeu e a reemissao sai por /notas-fiscais — nao daqui (ver o comentario do ramo de erro em
// ModalResultadoEmissao: reemitir sem saber se a SEFAZ autorizou a primeira arrisca nota em dobro).

interface FormaPag { id: number; nome: string; tipo: string; }
interface Props {
  clienteId: number;
  clienteNome: string;
  totalSaldo: number;
  onClose: () => void;
  onSucesso: () => void;
}

type TipoDoc = 'nfce' | 'nfe';
interface Resultado {
  fechamento_id: number; numero: string; total: number; tipo_documento: TipoDoc;
  nota: { emitida: boolean; erro?: string; dados?: ResultadoEmissaoData };
  titulos: number; vencimento: string; a_vista: boolean;
}

export function ModalFecharMes({ clienteId, clienteNome, totalSaldo, onClose, onSucesso }: Props) {
  const dialog = useDialog();
  const [formas, setFormas] = useState<FormaPag[]>([]);
  const [formaId, setFormaId] = useState<number | null>(null);
  const [dias, setDias] = useState('30');
  const [tipoDoc, setTipoDoc] = useState<TipoDoc>('nfce');
  // Cliente travado em A4 no cadastro (migration 0263): esta porta EMITE nota sem passar pelo caixa,
  // entao a trava do `finalizar` nao a alcanca — o `fechar-venda-cm` recusa no servidor e aqui a
  // opcao Consumidor nem aparece (oferecer o que o servidor rejeita e' pior que oferecer de menos).
  const [travadoA4, setTravadoA4] = useState(false);
  useEffect(() => {
    if (!clienteId) return;
    let vivo = true;
    api.get<{ travar_nota_a4?: boolean }>(`/api/clientes/${clienteId}`)
      .then((c) => { if (vivo && c?.travar_nota_a4) { setTravadoA4(true); setTipoDoc('nfe'); } })
      .catch(() => { /* o servidor ainda recusa; a tela so nao adianta o aviso */ });
    return () => { vivo = false; };
  }, [clienteId]);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [resultado, setResultado] = useState<Resultado | null>(null);

  // Depois do fechamento o Esc nao pode "cancelar": o fechamento ja valeu, entao ele
  // conclui (recarrega a lista) em vez de fechar seco.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || salvando) return;
      if (resultado) onSucesso(); else onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, onSucesso, salvando, resultado]);

  useEffect(() => {
    api.get<FormaPag[]>('/api/orcamentos/aux/formas-pagamento')
      .then((r) => setFormas(r.filter((f) => ['pix', 'dinheiro', 'crediario'].includes(f.tipo))))
      .catch(() => setErro('Nao foi possivel carregar as formas de pagamento.'));
  }, []);

  const formaSel = formas.find((f) => f.id === formaId) ?? null;
  const ehCrediario = formaSel?.tipo === 'crediario';

  async function confirmar() {
    if (salvando || formaId == null) return;
    // Portao humano antes do disparo fiscal: o clique em "Fechar o mes" emite nota de
    // verdade na SEFAZ e gera os titulos — nao ha desfazer barato depois disso.
    if (!(await dialog.confirmar('Confirma Emissão da Nota Fiscal?'))) return;
    setErro(null);
    setSalvando(true);
    try {
      const r = await api.post<Resultado>('/api/orcamentos/fechar-venda-cm', {
        cliente_id: clienteId,
        forma_pagamento_id: formaId,
        tipo_documento: tipoDoc,
        dias_vencimento: ehCrediario ? Number(dias) || 30 : undefined,
      });
      setResultado(r);
    } catch (e: any) {
      setErro(e?.message || 'Erro ao fechar o mes.');
      setSalvando(false);
    }
  }

  // ---- Fase RESULTADO ----
  // Reusa o ModalResultadoEmissao do caixa (imprimir DANFE / salvar PDF / enviar WhatsApp):
  // o fechamento CM emite nota como qualquer venda, entao a nota merece as mesmas acoes na
  // hora — sem isso o operador tinha que ir caçar o documento em Notas Fiscais.
  if (resultado) {
    const dados: ResultadoEmissaoData = resultado.nota.dados
      ?? { erro: resultado.nota.erro, xMotivo: resultado.nota.erro };
    return (
      <ModalResultadoEmissao
        tipo={resultado.tipo_documento}
        resultado={dados}
        clienteNome={clienteNome}
        clienteId={clienteId}
        valorTotal={resultado.total}
        avisoErro="O fechamento ja valeu (titulos e comissao gerados). Corrija o motivo e emita a nota pela aba Notas Fiscais."
        resumoExtra={
          <div className="mb-4 rounded-lg p-3 text-sm" style={{ backgroundColor: '#eef2f9', color: '#0f1b33' }}>
            <div className="flex items-center gap-2 font-bold" style={{ color: '#12294f' }}>
              <CalendarCheck size={16} /> Fechamento {resultado.numero} — {formatMoeda(resultado.total)}
            </div>
            <div className="mt-1.5 flex items-start gap-2" style={{ fontSize: 13 }}>
              <Landmark size={15} style={{ flexShrink: 0, marginTop: 2, color: '#12294f' }} />
              <span>
                {resultado.a_vista
                  ? <><b>{resultado.titulos} titulos</b> ja quitados em <b>Contas a Receber</b> (um por vendedor).</>
                  : <><b>{resultado.titulos} titulos</b> em aberto em <b>Contas a Receber</b>, vencendo em <b>{resultado.vencimento}</b> (um por vendedor).</>}
                <br />
                Este fechamento <b>nao passa pelo caixa</b> — nao ha nada a finalizar la.
              </span>
            </div>
          </div>
        }
        onConcluir={onSucesso}
      />
    );
  }

  // ---- Fase FORMULARIO ----
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md flex flex-col max-h-[90vh]">
        <div className="flex items-center gap-3 px-5 py-3.5 rounded-t-xl text-white" style={{ backgroundColor: '#12294f', borderBottom: '2px solid #1d4ed8' }}>
          <CalendarCheck size={18} />
          <div className="flex-1 min-w-0">
            <div className="font-bold text-base">Fechar o mes</div>
            <div className="text-xs truncate" style={{ color: '#a9bbe0' }}>{clienteNome}</div>
          </div>
          <button type="button" onClick={onClose} disabled={salvando} aria-label="Fechar"
            className="w-8 h-8 rounded-lg text-blue-100 hover:bg-white/10 disabled:opacity-50">✕</button>
        </div>

        <div className="p-5 overflow-y-auto">
          <div className="flex gap-2.5 items-start rounded-lg px-3 py-2.5 mb-4 text-sm" style={{ backgroundColor: '#f3f6fc', border: '1px solid #e2e7f0', color: '#47526b' }}>
            <span style={{ color: '#1d4ed8', fontWeight: 800 }}>✓</span>
            <span>Junta todo o material que ficou com o cliente numa <b>venda única</b> e emite <b>uma nota</b>. A comissão vai pra o vendedor de cada pedido. <b>Não</b> mexe no estoque (já saiu).</span>
          </div>

          {/* Total */}
          <div className="flex items-baseline justify-between mb-4 px-1">
            <span className="text-sm" style={{ color: '#8791a6' }}>Total a faturar</span>
            <span className="text-2xl font-extrabold" style={{ color: '#0f1b33' }}>{formatMoeda(totalSaldo)}</span>
          </div>

          {/* Forma de pagamento */}
          <div className="mb-4">
            <div className="text-xs font-bold uppercase tracking-wide mb-2" style={{ color: '#8791a6' }}>Forma de pagamento</div>
            <div className="grid grid-cols-3 gap-2">
              {formas.map((f) => {
                const sel = f.id === formaId;
                return (
                  <button key={f.id} type="button" onClick={() => setFormaId(f.id)}
                    className="px-2 py-2.5 rounded-lg text-sm font-bold border-2 transition-colors"
                    style={{
                      backgroundColor: sel ? '#fde68a' : '#fff',
                      color: sel ? '#000' : '#47526b',
                      borderColor: sel ? '#eab308' : '#e2e7f0',
                    }}>{f.nome}</button>
                );
              })}
            </div>
            {ehCrediario && (
              <div className="flex items-center gap-2 mt-3">
                <span className="text-sm" style={{ color: '#47526b' }}>Vencimento em</span>
                <input type="text" inputMode="numeric" value={dias}
                  onChange={(e) => setDias(e.target.value.replace(/\D/g, ''))}
                  className="w-16 text-center font-bold rounded-lg px-2 py-1.5 outline-none"
                  style={{ border: '1.5px solid #e2e7f0', color: '#0f1b33' }} />
                <span className="text-sm" style={{ color: '#47526b' }}>dias</span>
              </div>
            )}
          </div>

          {/* Tipo de nota */}
          <div className="mb-1">
            <div className="text-xs font-bold uppercase tracking-wide mb-2" style={{ color: '#8791a6' }}>Tipo de nota</div>
            {travadoA4 && (
              <div className="text-xs mb-2" style={{ color: '#8791a6' }}>
                {clienteNome} so recebe <b>Nota A4</b> — travado no cadastro do cliente
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              {(travadoA4
                ? ([['nfe', 'A4 (NF-e)']] as [TipoDoc, string][])
                : ([['nfce', 'Consumidor'], ['nfe', 'A4 (NF-e)']] as [TipoDoc, string][])
              ).map(([t, label]) => {
                const sel = t === tipoDoc;
                return (
                  <button key={t} type="button" onClick={() => setTipoDoc(t)}
                    className="px-2 py-2.5 rounded-lg text-sm font-bold border-2 transition-colors"
                    style={{
                      backgroundColor: sel ? '#fde68a' : '#fff',
                      color: sel ? '#000' : '#47526b',
                      borderColor: sel ? '#eab308' : '#e2e7f0',
                    }}>{label}</button>
                );
              })}
            </div>
          </div>

          {erro && <div className="mt-3 text-sm rounded-lg px-3 py-2" style={{ backgroundColor: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca' }}>{erro}</div>}
        </div>

        <div className="flex items-center gap-3 px-5 py-3.5 rounded-b-xl mt-auto" style={{ borderTop: '1px solid #e2e7f0', backgroundColor: '#fafbfe' }}>
          <div className="flex-1" />
          <button type="button" onClick={onClose} disabled={salvando}
            className="px-4 py-2 rounded-lg font-semibold text-sm disabled:opacity-50"
            style={{ backgroundColor: '#fff', border: '1.5px solid #e2e7f0', color: '#47526b' }}>Cancelar</button>
          <button type="button" onClick={() => void confirmar()} disabled={salvando || formaId == null}
            className="px-5 py-2 rounded-lg font-bold text-sm text-white flex items-center gap-2 disabled:opacity-50"
            style={{ backgroundColor: '#1d4ed8' }}>
            {salvando ? <Loader2 size={16} className="animate-spin" /> : <CalendarCheck size={16} />}
            Fechar o mes
          </button>
        </div>
      </div>
    </div>
  );
}
